import { useState } from "react";
import { theme, type as text } from "../../theme";
import { api, apiErrorMessage } from "../../api/client";
import { CRITICALITIES, input, secondaryBtn, type Option } from "./shared";

// Bulk actions on the asset register: confirm as reviewed, set the owner, set the criticality —
// on the ticked rows, or on every row shown when none is ticked. POST /resilience/assets/bulk
// checks every id is this organisation's asset and the owner this organisation's member.

export function AssetBulkBar({ selectedIds, shownIds, members, unreviewedIds, onDone, onError }: {
  selectedIds: string[];
  shownIds: string[];
  members: Option[];
  /** Of the target rows, the discovered ones still awaiting review. */
  unreviewedIds: (ids: string[]) => string[];
  onDone: (message: string) => Promise<void>;
  onError: (message: string) => void;
}) {
  const [owner, setOwner] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [criticality, setCriticality] = useState("");
  const [busy, setBusy] = useState(false);
  const ids = selectedIds.length ? selectedIds : shownIds;
  const scope = selectedIds.length ? `${selectedIds.length} selected` : shownIds.length === 1 ? "the 1 shown" : `all ${shownIds.length} shown`;
  const toReview = unreviewedIds(ids);

  async function run(body: Record<string, unknown>, label: string) {
    if (!ids.length) return;
    if (!selectedIds.length && !window.confirm(`Apply to ${shownIds.length === 1 ? "the 1 asset" : `all ${shownIds.length} assets`} shown?`)) return;
    setBusy(true);
    try {
      const r = await api.post<{ updated: number }>("/resilience/assets/bulk", { ids, ...body });
      await onDone(`${label} on ${r.updated} asset${r.updated === 1 ? "" : "s"}.`);
      setOwner(""); setOwnerName(""); setCriticality("");
    } catch (e) { onError(apiErrorMessage(e, "Could not update the assets")); }
    finally { setBusy(false); }
  }

  async function review() {
    if (!toReview.length) return;
    setBusy(true);
    try {
      const r = await api.post<{ reviewed: number }>("/resilience/assets/review", { ids: toReview });
      await onDone(`${r.reviewed} asset${r.reviewed === 1 ? "" : "s"} confirmed as reviewed.`);
    } catch (e) { onError(apiErrorMessage(e, "Could not confirm the assets")); }
    finally { setBusy(false); }
  }

  const small: React.CSSProperties = { ...input, width: "auto", fontSize: text.hint.fontSize, padding: "5px 8px" };
  return (
    <div style={{ ...text.hint, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", padding: "8px 12px", borderRadius: 3, background: theme.color.panelAlt, border: "1px solid var(--c-border)" }}>
      <strong style={{ color: theme.color.text }}>Bulk: {scope}</strong>
      <button type="button" style={secondaryBtn} disabled={busy || toReview.length === 0} onClick={review} title="Confirm the discovered assets among them as reviewed, keeping criticality and owner as they are">
        Confirm reviewed{toReview.length ? ` (${toReview.length})` : ""}
      </button>
      <span style={{ width: 1, alignSelf: "stretch", background: "var(--c-border)" }} />
      <select value={criticality} onChange={(e) => setCriticality(e.target.value)} style={small} aria-label="Criticality to set">
        <option value="">Set criticality</option>
        {CRITICALITIES.map((c) => <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}
      </select>
      <button type="button" style={secondaryBtn} disabled={busy || !criticality || !ids.length} onClick={() => run({ criticality }, `Criticality set to ${criticality}`)}>Apply</button>
      <span style={{ width: 1, alignSelf: "stretch", background: "var(--c-border)" }} />
      <select value={owner} onChange={(e) => setOwner(e.target.value)} style={small} aria-label="Owner to set">
        <option value="">Set owner</option>
        {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        <option value="__name__">Someone who is not a member</option>
        <option value="__clear__">Clear the owner</option>
      </select>
      {owner === "__name__" && <input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder="Owner's name" style={{ ...small, width: 160 }} aria-label="Owner's name" />}
      <button
        type="button" style={secondaryBtn}
        disabled={busy || !owner || !ids.length || (owner === "__name__" && !ownerName.trim())}
        onClick={() => owner === "__clear__"
          ? run({ ownerUserId: null, ownerName: null }, "Owner cleared")
          : owner === "__name__"
            ? run({ ownerUserId: null, ownerName: ownerName.trim() }, `Owner set to ${ownerName.trim()}`)
            : run({ ownerUserId: owner, ownerName: null }, `Owner set to ${members.find((m) => m.id === owner)?.name ?? "the member"}`)}
      >Apply</button>
      {busy && <span>Working…</span>}
    </div>
  );
}
